import { useEffect, useRef, useState } from 'react';
import {
  ACCOUNTS_REVIEW_REFUSAL,
  isChangeField,
  type ChangeSet,
  type ChangedField,
} from '@/domain';
import {
  useAccountsReviewDetail,
  useAccountsReviewed,
  useAccountsUnreview,
} from '@/application/usecases/useOrders';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { formatDay, formatInstant } from '@/shared/format/instant';
import { formatAmount } from '@/shared/format/money';
import { BeforeAfter, type BeforeAfterEntry, type BeforeAfterWords } from '@/ui/data/BeforeAfter';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { useConfirmAction } from '../shell/useConfirmAction';
import styles from './ChangesSheet.module.css';

const t = strings.payments;

const WORDS: BeforeAfterWords = {
  was: t.was,
  now: t.now,
  hidden: t.hiddenChange,
  showAll: t.showAll,
  showLess: t.showLess,
};

/**
 * A sum as the receipt prints it. The case value travels as the plain number,
 * written out, and is the one field of a change the screen words itself.
 */
function money(text: string): string {
  const value = Number(text);
  return Number.isFinite(value) ? formatAmount(value) : text;
}

/** One changed field as a row: its name in the lab's words, and its two values. */
function entryOf(field: ChangedField): BeforeAfterEntry {
  const worded = (value: string | null) =>
    value === null || field.field !== 'value' ? value : money(value);
  return {
    // A key this build has not heard of is printed as it arrives, rather than dropped.
    label: isChangeField(field.field) ? t.changeField[field.field] : field.field,
    before: worded(field.before),
    after: worded(field.after),
    hidden: field.hidden,
  };
}

/** Each edit of the case, oldest first: who made it and when, then its fields before and after. */
function ChangeSets({ sets }: { sets: readonly ChangeSet[] }) {
  return (
    <ol className={styles.sets} aria-label={t.whatChanged}>
      {sets.map((set, index) => (
        // A change waiting from before changes were kept has no id; there is at most one.
        <li key={set.id ?? `unkept-${String(index)}`} className={styles.set}>
          <p className={styles.who}>{t.changedBy(set.byName, formatInstant(set.at))}</p>
          {set.fields.length === 0 ? (
            <>
              {set.summary !== '' && <p className={styles.summary}>{set.summary}</p>}
              <p className={styles.legacy}>{t.legacyNote}</p>
            </>
          ) : (
            <BeforeAfter entries={set.fields.map(entryOf)} words={WORDS} />
          )}
        </li>
      ))}
    </ol>
  );
}

/** Which list the sheet was opened from: what waits to be reviewed, or what a review covered. */
export type ChangesMode = 'waiting' | 'reviewed';

export interface ChangesSheetProps {
  jobOrderNo: string;
  mode: ChangesMode;
  onClose: () => void;
  /** The case was marked as reviewed. The screen closes the sheet and offers Undo. */
  onReviewed: (jobOrderNo: string) => void;
  /** The review was taken back. The screen closes the sheet and says so. */
  onNotReviewed: (jobOrderNo: string) => void;
}

/**
 * What changed on a case since it was priced, before and after. The request of
 * 5 October 2026 (third), section 3: only the changed fields, the old value
 * struck through and the new one highlighted, who made each edit and when, and
 * the job order in the title.
 *
 * Opened from the Changed list it is the confirmation of the review as well,
 * built on the shared `ConfirmSheet`: section 2 of the same request asks for a
 * question before a case is marked as reviewed, and the question is put where
 * the changes are read, so nobody answers it without having seen them and
 * nobody is asked twice. Mark as reviewed sends back the `since` the sheet
 * read; when the case was edited again in the meantime the server refuses, the
 * sheet stays open with the server's words, and the new change is already on it.
 *
 * Opened from the Reviewed list it reads only: who reviewed the case and when,
 * and the changes that review covered. Mark as not reviewed asks first, through
 * the shared confirmation, over this sheet, so Cancel comes back to it.
 */
export function ChangesSheet({
  jobOrderNo,
  mode,
  onClose,
  onReviewed,
  onNotReviewed,
}: ChangesSheetProps) {
  const { data: detail, isPending, isError } = useAccountsReviewDetail(jobOrderNo);
  const review = useAccountsReviewed();
  const unreview = useAccountsUnreview();
  const ask = useConfirmAction();

  const [error, setError] = useState<string | null>(null);
  const refusal = useRef<HTMLParagraphElement>(null);

  // The refusal is the last thing in a sheet that may be scrolled: bring it under the eye.
  useEffect(() => {
    if (error !== null) refusal.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error]);

  function markReviewed() {
    if (detail === undefined) return false;
    setError(null);
    /*
     * By the promise and not by callbacks on the call: the sheet may be closed
     * by its head while the review is on its way, and the screen must still
     * offer Undo for a review that went through.
     */
    review.mutateAsync({ jobOrderNo, seen: detail.since }).then(
      () => onReviewed(jobOrderNo),
      // The server's words. The changes on screen have been read again by now.
      (failure: Error) => setError(failureWords(failure)),
    );
    return true;
  }

  function markNotReviewed() {
    ask(
      t.notReviewedStatement(jobOrderNo),
      () => unreview.mutateAsync(jobOrderNo).then(() => onNotReviewed(jobOrderNo)),
      { confirmLabel: t.markNotReviewed },
    );
  }

  const title = t.changesTitle(jobOrderNo);
  const loading = isPending && <SkeletonRows rows={3} label={t.changesLoading} />;
  const failed = isError && detail === undefined && (
    <p className={styles.note}>{strings.errors.server}</p>
  );

  if (mode === 'waiting') {
    const waiting = detail?.waiting ?? [];
    return (
      <ConfirmSheet
        title={title}
        statement={t.reviewStatement(jobOrderNo)}
        confirmLabel={t.markReviewed}
        cancelLabel={t.close}
        closeLabel={strings.sheet.close}
        onConfirm={markReviewed}
        onCancel={onClose}
        busy={review.isPending}
        // Nothing is reviewed unread: not while the changes are arriving, and not when none waits.
        confirmDisabled={waiting.length === 0}
      >
        {loading}
        {failed}
        {detail !== undefined &&
          (waiting.length === 0 ? (
            // Somebody else was quicker. The words are the server's own for it.
            <p className={styles.note}>{ACCOUNTS_REVIEW_REFUSAL.nothingToReview}</p>
          ) : (
            <ChangeSets sets={waiting} />
          ))}
        {error !== null && (
          <p ref={refusal} className={modalStyles.error} role="alert">
            {error}
          </p>
        )}
      </ConfirmSheet>
    );
  }

  const reviewed = detail?.reviewed ?? null;
  return (
    <BottomSheet
      title={title}
      onClose={onClose}
      closeLabel={strings.sheet.close}
      footer={
        <div className={sheetStyles.actions}>
          <Button onClick={onClose}>{t.close}</Button>
          {reviewed !== null && (
            <Button onClick={markNotReviewed} disabled={unreview.isPending}>
              {t.markNotReviewed}
            </Button>
          )}
        </div>
      }
    >
      {loading}
      {failed}
      {detail !== undefined &&
        (reviewed === null ? (
          <p className={styles.note}>{t.notReviewedNow}</p>
        ) : (
          <>
            <p className={styles.reviewedBy}>
              {t.reviewedBy(reviewed.byName, formatDay(reviewed.at))}
            </p>
            <ChangeSets sets={reviewed.sets} />
          </>
        ))}
    </BottomSheet>
  );
}
